/**
 * SeasonView — масштаб «Сезон» слота План (MOBILE_NAV_ARCHITECTURE.md раздел 2).
 *
 * От сегодня до цели: фазы подготовки полосой, скраббер микроциклов,
 * карточка цели с обратным отсчётом, действия сезона. За 7 дней до старта
 * сверху появляется вход в Центр гонки (takeover по спеке: сезон в race
 * week — это и есть гонка).
 *
 * Материал переиспользован: PhaseStrip и PlanWeekScrubber уже жили в
 * context-шите страницы — сезон поднимает их в полноценный масштаб.
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CaretRight, Flag, PencilSimple, ArrowsClockwise, MapPin } from "@phosphor-icons/react";

import type { GoalBrief } from "../../dashboardApi";
import { PhaseStrip, type PhaseSpec } from "./PhaseStrip";
import { PlanWeekScrubber } from "./PlanWeekScrubber";

export interface SeasonViewProps {
  phases: PhaseSpec[];
  todayISO: string;
  raceDateISO: string | null;
  daysToRace: number | null;
  goal: GoalBrief | null;
  currentWeek: number | null;
  totalWeeksLabel?: string;
}

export function SeasonView({
  phases,
  todayISO,
  raceDateISO,
  daysToRace,
  goal,
  currentWeek,
  totalWeeksLabel,
}: SeasonViewProps) {
  const { t, i18n } = useTranslation("plan");
  const isRaceWeek = daysToRace != null && daysToRace >= 0 && daysToRace <= 7;
  // «2027-04-01» — сырой ISO в пользовательской копии; дата цели говорит
  // на языке интерфейса.
  const raceDateLabel = raceDateISO
    ? new Date(raceDateISO + "T00:00:00").toLocaleDateString(i18n.language, {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <div className="space-y-3" data-testid="plan-season-view">
      {/* Race week — сезон сейчас и есть гонка */}
      {isRaceWeek && (
        <Link
          to="/race-week"
          data-testid="plan-season-racecenter"
          className="indigo-card px-4 py-3.5 flex items-center gap-3 hover:bg-bg-raised transition-colors ring-1 ring-inset ring-accent/40"
        >
          <span className="w-10 h-10 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0">
            <Flag size={20} weight="fill" aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-fg-primary">
              {t("season.race_center", { defaultValue: "Центр гонки" })}
            </span>
            <span className="block text-[12px] text-fg-secondary">
              {t("season.race_center_hint", {
                defaultValue: "Пейсинг · питание · погода · чек-лист",
              })}
            </span>
          </span>
          <CaretRight size={14} className="text-fg-muted shrink-0" aria-hidden />
        </Link>
      )}

      {/* Карточка цели: что впереди и сколько до него */}
      {goal && raceDateISO ? (
        <Link
          to="/goal"
          data-testid="plan-season-goal"
          className="indigo-card px-4 py-3.5 block hover:bg-bg-raised transition-colors"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-fg-muted">
              {t("season.goal", { defaultValue: "Цель сезона" })}
            </div>
            {daysToRace != null && daysToRace >= 0 && (
              <div className="text-[11px] font-mono text-accent" data-testid="plan-season-countdown">
                {t("season.days_left", { defaultValue: "через {{n}} дн", n: daysToRace })}
              </div>
            )}
          </div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-[19px] font-semibold text-fg-primary truncate">
              {goal.race_distance ??
                t("season.goal_fallback", { defaultValue: "Главный старт" })}
            </span>
            <span className="text-[12px] text-fg-muted font-mono shrink-0">{raceDateLabel}</span>
          </div>
        </Link>
      ) : (
        <Link
          to="/events"
          data-testid="plan-season-no-goal"
          className="indigo-card px-4 py-3.5 flex items-center gap-3 hover:bg-bg-raised transition-colors"
        >
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-fg-primary">
              {t("season.no_goal", { defaultValue: "Цели пока нет" })}
            </span>
            <span className="block text-[12px] text-fg-secondary">
              {t("season.no_goal_hint", { defaultValue: "Сезон строится от старта — выбери его" })}
            </span>
          </span>
          <CaretRight size={14} className="text-fg-muted shrink-0" aria-hidden />
        </Link>
      )}

      {/* Фазы не построены — сезон молчал без единого слова, почему он
          пуст (v212). Данных реально нет (plan_phases пуст), поэтому не
          рисуем выдуманную периодизацию, а называем путь: генератор
          плана строит фазы. */}
      {phases.length === 0 && goal && raceDateISO && (
        <Link
          to="/plan/generate"
          data-testid="plan-season-no-phases"
          className="indigo-card px-4 py-3.5 flex items-center gap-3 hover:bg-bg-raised transition-colors"
        >
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-fg-primary">
              {t("season.no_phases", { defaultValue: "Фазы сезона ещё не построены" })}
            </span>
            <span className="block text-[12px] text-fg-secondary">
              {t("season.no_phases_hint", {
                defaultValue:
                  "Собери план — база, развитие, пик и подводка встанут от даты старта",
              })}
            </span>
          </span>
          <CaretRight size={14} className="text-fg-muted shrink-0" aria-hidden />
        </Link>
      )}

      {/* Фазы подготовки полосой + скраббер микроциклов */}
      {currentWeek != null && phases.length > 0 && (
        <PlanWeekScrubber phases={phases} currentWeek={currentWeek} raceDateISO={raceDateISO} />
      )}
      {phases.length > 0 && (
        <PhaseStrip
          phases={phases}
          todayISO={todayISO}
          raceDateISO={raceDateISO}
          totalWeeksLabel={totalWeeksLabel}
        />
      )}

      {/* Действия сезона — спека: сдвинуть цель, промежуточный старт, пересобрать */}
      <div className="grid grid-cols-3 gap-2" data-testid="plan-season-actions">
        <Link
          to="/goal/edit"
          data-testid="plan-season-edit-goal"
          className="indigo-card px-2 py-2.5 flex flex-col items-center gap-1 hover:bg-bg-raised transition-colors"
        >
          <PencilSimple size={16} className="text-fg-secondary" aria-hidden />
          <span className="text-[10.5px] text-fg-secondary text-center leading-tight">
            {t("season.act_goal", { defaultValue: "Сдвинуть цель" })}
          </span>
        </Link>
        <Link
          to="/events"
          data-testid="plan-season-add-race"
          className="indigo-card px-2 py-2.5 flex flex-col items-center gap-1 hover:bg-bg-raised transition-colors"
        >
          <MapPin size={16} className="text-fg-secondary" aria-hidden />
          <span className="text-[10.5px] text-fg-secondary text-center leading-tight">
            {t("season.act_race", { defaultValue: "Добавить старт" })}
          </span>
        </Link>
        <Link
          to="/plan/generate"
          data-testid="plan-season-rebuild"
          className="indigo-card px-2 py-2.5 flex flex-col items-center gap-1 hover:bg-bg-raised transition-colors"
        >
          <ArrowsClockwise size={16} className="text-fg-secondary" aria-hidden />
          <span className="text-[10.5px] text-fg-secondary text-center leading-tight">
            {t("season.act_rebuild", { defaultValue: "Пересобрать" })}
          </span>
        </Link>
      </div>
    </div>
  );
}
